import Phaser from 'phaser';
import { coinLabel } from '../../config/coinLabel';
import { LAST_LEVEL_ID } from '../../config/levels';
import {
  EVOLVE_TOWERS,
  EVOLVE_PRICES,
  availableTowers,
  type EvolveTowerType,
} from '../../config/unlocks';
import { TOWER_NAMES } from '../../config/displayNames';
import { ProgressService } from '../../services/progressService';
import { saveStorage } from '../saveStorage';
import { sweepAndToastAchievements } from '../../ui/achievementToast';
import { spinCoin } from '../coin';
import { getAudioService } from '../audio/AudioService';
import { addMuteButton } from '../audio/audioControls';
import { addFullscreenButton } from '../screenControls';
import { installDebugReturn } from '../debugMode';

const GOLD = 0xf5c518;
const GOLD_HEX = '#f5c518';
/** Tube glass greens. */
const TUBE_FILL = 0x39ff88;
const TUBE_STROKE = 0x6effa8;
/** Brushed-steel cap palette: body, top specular, bottom shade, outline, rivet. */
const CAP_STEEL = 0x8f97a3;
const CAP_SHEEN = 0xdfe5ec;
const CAP_SHADE = 0x4b525d;
const CAP_EDGE = 0x262a31;
const CAP_RIVET = 0x6c737e;
/** Tube geometry: three tubes per page, one tower each. */
const TUBE_XS = [340, 640, 940];
const TUBE_Y = 380;
const TUBE_W = 210;
const TUBE_H = 330;
/** Cap footprint — wider than the glass so it reads as a clamp. */
const CAP_W = TUBE_W + 20;
const CAP_H = 28;
/** EVOLVE button row under the tubes (fixed for the e2e click targets). */
const BUTTON_Y = 595;

/**
 * EvolveScene — spend persistent Hambi coins to permanently Evolve a tower to
 * its 4th tier. Towers float in green suspension tubes, three per page; a
 * tower must already be unlocked to evolve. Reached from the ShopScene pill
 * (only visible once all of Chapter 2 is cleared); BACK returns there.
 */
export class EvolveScene extends Phaser.Scene {
  private progress!: ProgressService;
  private balanceText!: Phaser.GameObjects.Text;
  /** Current page's tubes (torn down and rebuilt after a purchase / page turn). */
  private tubesContainer?: Phaser.GameObjects.Container;
  /** Transient "not enough coins" / "locked" hint. */
  private hint?: Phaser.GameObjects.Text;
  /** True while the confirm dialog is up — tube buttons ignore clicks. */
  private confirmOpen = false;
  /** Current page index into pagesData. */
  private page = 0;
  private pagesData: EvolveTowerType[][] = [];
  private leftArrow?: { box: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text };
  private rightArrow?: { box: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text };
  private pageLabel?: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'EvolveScene' });
  }

  create(): void {
    const cx = 640;
    this.progress = new ProgressService(saveStorage(), LAST_LEVEL_ID);
    this.tubesContainer = undefined;
    this.hint = undefined;
    this.confirmOpen = false;
    this.page = 0;
    // Three tubes per page: [bomb, ninja, wise] then [sensei, cheese, patty].
    this.pagesData = [];
    for (let i = 0; i < EVOLVE_TOWERS.length; i += 3) {
      this.pagesData.push(EVOLVE_TOWERS.slice(i, i + 3));
    }
    this.leftArrow = undefined;
    this.rightArrow = undefined;
    this.pageLabel = undefined;

    // Green lab backdrop (real bg-evolve.png when generated, procedural
    // placeholder otherwise), dimmed like the Shop's.
    this.add.image(640, 360, 'bg-evolve').setDisplaySize(1280, 720);
    this.add.rectangle(640, 360, 1280, 720, 0x000000, 0.35);

    // ── Header ──────────────────────────────────────────────────────────────
    this.add
      .text(cx, 48, 'EVOLVE', { fontSize: '38px', color: GOLD_HEX, fontStyle: 'bold' })
      .setOrigin(0.5);
    this.add
      .text(cx, 84, 'PERMANENTLY EVOLVE A HAMBI TO TIER 4', {
        fontSize: '15px',
        color: '#b8e8c8',
      })
      .setOrigin(0.5)
      .setShadow(0, 2, '#000000', 4);

    // Back button — same styling/position as ShopScene's.
    const backBtn = this.add
      .text(80, 50, '← BACK', {
        fontSize: '22px',
        color: '#cfe0bd',
        backgroundColor: '#15131f',
        padding: { x: 16, y: 12 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    backBtn.on('pointerover', () => backBtn.setColor(GOLD_HEX));
    backBtn.on('pointerout', () => backBtn.setColor('#cfe0bd'));
    backBtn.on('pointerup', () => this.scene.start('ShopScene'));

    // Coin balance — same spot as ShopScene's top bar.
    const coinIcon = this.add.image(960, 50, 'coin').setDisplaySize(34, 34).setDepth(5);
    spinCoin(this, coinIcon);
    this.balanceText = this.add
      .text(982, 50, coinLabel(this.progress.coins()), {
        fontSize: '22px',
        color: GOLD_HEX,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(5);

    addMuteButton(this, 1245, 50, getAudioService(this));
    addFullscreenButton(this, 1100, 50);

    // ── Tubes + pager + footer ───────────────────────────────────────────────
    this.renderTubes();
    this.renderPager();
    this.add
      .text(cx, 690, 'Evolutions are permanent. A tower must be unlocked before it can evolve.', {
        fontSize: '15px',
        color: '#b8c4c6',
        fontStyle: 'italic',
      })
      .setOrigin(0.5)
      .setShadow(0, 2, '#000000', 4);

    installDebugReturn(this); // Esc → DemoScene when launched via ?debug
  }

  /** (Re)draw the current page's suspension tubes to match coins + evolutions. */
  private renderTubes(): void {
    this.tubesContainer?.destroy();
    const container = this.add.container(0, 0).setDepth(1);
    this.tubesContainer = container;
    // A tower must be unlocked (episode-granted, or one of the extras —
    // shop-bought, win-granted, challenge-earned) before its Evolution is
    // offered.
    const unlocked = new Set(
      availableTowers(this.progress.enteredLevels(), this.progress.unlockedExtras()),
    );
    const types = this.pagesData[this.page] ?? [];
    types.forEach((type, i) =>
      this.makeTube(TUBE_XS[i], TUBE_Y, type, unlocked.has(type), container),
    );
  }

  /**
   * One brushed-steel collar, drawn from `topY` down: mid-grey body, a bright
   * specular band across the upper half, a dark shade band under it, a hairline
   * highlight, a dark outline and a row of rivets — so the glass reads as being
   * clamped between two metal end caps rather than tinted plastic.
   */
  private drawMetalCap(g: Phaser.GameObjects.Graphics, cx: number, topY: number): void {
    const left = cx - CAP_W / 2;
    g.fillStyle(CAP_STEEL, 1);
    g.fillRoundedRect(left, topY, CAP_W, CAP_H, 7);
    // Specular sheen along the top, shadow along the bottom.
    g.fillStyle(CAP_SHEEN, 0.55);
    g.fillRect(left + 4, topY + 4, CAP_W - 8, 6);
    g.fillStyle(CAP_SHADE, 0.75);
    g.fillRect(left + 4, topY + CAP_H - 10, CAP_W - 8, 6);
    // Hairline highlight right under the sheen sells the curved metal.
    g.fillStyle(0xffffff, 0.35);
    g.fillRect(left + 8, topY + 11, CAP_W - 16, 2);
    g.lineStyle(2, CAP_EDGE, 1);
    g.strokeRoundedRect(left, topY, CAP_W, CAP_H, 7);
    // Rivets across the collar face.
    const rivetY = topY + CAP_H / 2 + 1;
    for (let i = 0; i < 5; i++) {
      const rx = left + 22 + i * ((CAP_W - 44) / 4);
      g.fillStyle(CAP_RIVET, 1);
      g.fillCircle(rx, rivetY, 3.5);
      g.fillStyle(CAP_SHEEN, 0.8);
      g.fillCircle(rx - 1, rivetY - 1, 1.5);
    }
  }

  /** One suspension tube: glass, caps, bubbles, tower, price, and its action. */
  private makeTube(
    x: number,
    y: number,
    type: EvolveTowerType,
    isUnlocked: boolean,
    container: Phaser.GameObjects.Container,
  ): void {
    const evolved = this.progress.hasEvolved(type);
    const price = EVOLVE_PRICES[type];
    const affordable = this.progress.coins() >= price;
    const top = y - TUBE_H / 2;
    const bottom = y + TUBE_H / 2;

    // Glass body — green translucent glass; the stroke goes rich green once evolved.
    const glass = this.add.graphics();
    glass.fillStyle(TUBE_FILL, 0.14);
    glass.fillRoundedRect(x - TUBE_W / 2, top, TUBE_W, TUBE_H, 26);
    glass.lineStyle(2, evolved ? 0x4caf50 : TUBE_STROKE, evolved ? 0.95 : 0.8);
    glass.strokeRoundedRect(x - TUBE_W / 2, top, TUBE_W, TUBE_H, 26);
    // Glass shine down the left side.
    glass.fillStyle(0xffffff, 0.08);
    glass.fillRoundedRect(x - TUBE_W / 2 + 12, top + 16, 26, TUBE_H - 32, 12);
    container.add(glass);

    // Brushed-steel caps clamping the glass top and bottom.
    const caps = this.add.graphics();
    this.drawMetalCap(caps, x, top - 20);
    this.drawMetalCap(caps, x, bottom - 8);
    container.add(caps);

    // Rising bubbles — small tweened circles inside the glass.
    for (let i = 0; i < 5; i++) {
      const bx = x - TUBE_W / 2 + 30 + ((i * 37) % (TUBE_W - 60));
      const by = bottom - 30 - ((i * 53) % 80);
      const bubble = this.add.circle(bx, by, 3 + (i % 3), TUBE_FILL, 0.25);
      container.add(bubble);
      this.tweens.add({
        targets: bubble,
        y: top + 40,
        alpha: 0,
        duration: 2600 + i * 700,
        repeat: -1,
        delay: i * 420,
      });
    }

    // Tower name on the glass, under the top cap.
    container.add(
      this.add
        .text(x, top + 34, TOWER_NAMES[type], {
          fontSize: '18px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setShadow(0, 2, '#000000', 4),
    );

    // The evolved (tier-4) form floats in the tube.
    const art = this.add.image(x, y + 6, `tower-${type}-4`).setDisplaySize(140, 140);
    if (!isUnlocked) art.setTint(0x223028); // dark silhouette until unlocked
    container.add(art);
    if (isUnlocked && !evolved) {
      this.tweens.add({
        targets: art,
        y: y - 2,
        duration: 1600,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    // Price at the bottom of the tube (or the tube's state when not buyable).
    if (evolved) {
      container.add(
        this.add
          .text(x, bottom - 34, 'EVOLVED', {
            fontSize: '20px',
            color: '#4caf50',
            fontStyle: 'bold',
          })
          .setOrigin(0.5)
          .setShadow(0, 2, '#000000', 4),
      );
    } else if (!isUnlocked) {
      container.add(
        this.add
          .text(x, bottom - 34, 'LOCKED', { fontSize: '20px', color: '#8a969c', fontStyle: 'bold' })
          .setOrigin(0.5)
          .setShadow(0, 2, '#000000', 4),
      );
    } else {
      container.add(this.add.image(x - 30, bottom - 34, 'coin').setDisplaySize(26, 26));
      container.add(
        this.add
          .text(x - 12, bottom - 34, `${price}`, {
            fontSize: '20px',
            color: affordable ? GOLD_HEX : '#7f8c8d',
            fontStyle: 'bold',
          })
          .setOrigin(0, 0.5)
          .setShadow(0, 2, '#000000', 4),
      );
    }

    // Action under the tube: the shop-style EVOLVE button, or a state label.
    if (evolved) {
      container.add(
        this.add
          .text(x, BUTTON_Y, '✓ EVOLVED', {
            fontSize: '22px',
            color: '#4caf50',
            fontStyle: 'bold',
          })
          .setOrigin(0.5)
          .setShadow(0, 2, '#000000', 4),
      );
      return;
    }
    if (!isUnlocked) {
      container.add(
        this.add
          .text(x, BUTTON_Y, 'UNLOCK THIS HAMBI FIRST', {
            fontSize: '14px',
            color: '#8a969c',
            fontStyle: 'italic',
          })
          .setOrigin(0.5)
          .setShadow(0, 2, '#000000', 4),
      );
      return;
    }

    const btn = this.add
      .rectangle(x, BUTTON_Y, 150, 52, affordable ? GOLD : 0x2a2a3a)
      .setStrokeStyle(2, affordable ? 0xfff3b0 : 0x555a66)
      .setInteractive({ useHandCursor: affordable })
      .setDepth(2);
    container.add(btn);
    container.add(
      this.add
        .text(x, BUTTON_Y, 'EVOLVE', {
          fontSize: '20px',
          color: affordable ? '#1a140a' : '#7f8c8d',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(3),
    );
    if (affordable) {
      btn.on('pointerover', () => btn.setFillStyle(0xffd83a));
      btn.on('pointerout', () => btn.setFillStyle(GOLD));
    }
    btn.on('pointerup', () => {
      if (this.confirmOpen) return;
      if (affordable) this.showConfirm(type, price);
      else this.flashHint('Not enough coins — win episodes to earn more.');
    });
  }

  /** Build the page arrows + indicator (same pattern as the Shop's pager). */
  private renderPager(): void {
    if (this.pagesData.length <= 1) return;
    const y = 655;
    const mkArrow = (x: number, glyph: string, dir: -1 | 1) => {
      const box = this.add
        .rectangle(x, y, 60, 48, 0x15131f, 0.85)
        .setStrokeStyle(1, GOLD, 0.5)
        .setInteractive({ useHandCursor: true })
        .setDepth(4);
      const label = this.add
        .text(x, y, glyph, { fontSize: '26px', color: GOLD_HEX, fontStyle: 'bold' })
        .setOrigin(0.5)
        .setDepth(5);
      box.on('pointerup', () => {
        if (this.confirmOpen) return;
        const next = Math.min(Math.max(this.page + dir, 0), this.pagesData.length - 1);
        if (next === this.page) return;
        this.page = next;
        this.renderTubes();
        this.updatePager();
      });
      return { box, label };
    };
    this.leftArrow = mkArrow(560, '◀', -1);
    this.rightArrow = mkArrow(720, '▶', 1);
    this.pageLabel = this.add
      .text(640, y, '', { fontSize: '18px', color: '#cfe0bd', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setDepth(5);
    this.updatePager();
  }

  /** Sync the page indicator + dim the arrow that can't move any further. */
  private updatePager(): void {
    if (!this.pageLabel) return;
    this.pageLabel.setText(`${this.page + 1} / ${this.pagesData.length}`);
    const atFirst = this.page === 0;
    const atLast = this.page === this.pagesData.length - 1;
    this.leftArrow?.label.setAlpha(atFirst ? 0.3 : 1);
    this.leftArrow?.box.setAlpha(atFirst ? 0.4 : 0.85);
    this.rightArrow?.label.setAlpha(atLast ? 0.3 : 1);
    this.rightArrow?.box.setAlpha(atLast ? 0.4 : 0.85);
  }

  /** Transient footer hint (e.g. can't afford). */
  private flashHint(msg: string): void {
    this.hint?.destroy();
    this.hint = this.add
      .text(640, 672, msg, { fontSize: '15px', color: '#e8a0a0', fontStyle: 'italic' })
      .setOrigin(0.5)
      .setDepth(50);
    this.time.delayedCall(1800, () => {
      this.hint?.destroy();
      this.hint = undefined;
    });
  }

  /** Evolution confirm — mirrors the Shop's purchase confirm, gold-trimmed. */
  private showConfirm(type: EvolveTowerType, price: number): void {
    const W = 1280;
    const H = 720;
    const D = 100;
    this.confirmOpen = true;
    const objects: Phaser.GameObjects.GameObject[] = [];

    objects.push(
      this.add
        .rectangle(W / 2, H / 2, W, H, 0x000000, 0.7)
        .setInteractive()
        .setDepth(D),
    );
    objects.push(
      this.add
        .rectangle(W / 2, H / 2, 560, 260, 0x1a1726, 1)
        .setStrokeStyle(2, GOLD)
        .setDepth(D + 1),
    );
    objects.push(
      this.add
        .text(W / 2, H / 2 - 76, `Evolve ${TOWER_NAMES[type]}?`, {
          fontSize: '28px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    objects.push(
      this.add
        .text(
          W / 2,
          H / 2 - 22,
          `${price} Hambi coins · permanent Evolution.\nUnlocks its Tier 4 upgrade in every episode and the Arena.`,
          { fontSize: '16px', color: '#9aa6ad', align: 'center', lineSpacing: 4 },
        )
        .setOrigin(0.5)
        .setDepth(D + 2),
    );

    const teardown = () => {
      objects.forEach((o) => o.destroy());
      this.confirmOpen = false;
    };

    // Confirm (green) — at (520, 430) for the e2e click.
    const confirmBg = this.add
      .rectangle(W / 2 - 120, H / 2 + 70, 200, 52, 0x1a6b1a)
      .setStrokeStyle(2, 0x4caf50)
      .setInteractive({ useHandCursor: true })
      .setDepth(D + 2);
    objects.push(confirmBg);
    objects.push(
      this.add
        .text(W / 2 - 120, H / 2 + 70, 'EVOLVE', {
          fontSize: '20px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 3),
    );
    confirmBg.on('pointerup', () => {
      const ok = this.progress.evolveTower(type, price);
      teardown();
      if (ok) {
        // Evolution achievement (and any other newly satisfied state award).
        sweepAndToastAchievements(saveStorage());
        this.balanceText.setText(coinLabel(this.progress.coins()));
        this.renderTubes();
      }
    });

    // Cancel — at (760, 430).
    const cancelBg = this.add
      .rectangle(W / 2 + 120, H / 2 + 70, 200, 52, 0x2a2a3a)
      .setStrokeStyle(2, 0x6666aa)
      .setInteractive({ useHandCursor: true })
      .setDepth(D + 2);
    objects.push(cancelBg);
    objects.push(
      this.add
        .text(W / 2 + 120, H / 2 + 70, 'CANCEL', {
          fontSize: '20px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 3),
    );
    cancelBg.on('pointerup', teardown);
  }
}
